import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import { useSente } from '../hooks/useSente';

/** Dispatch this window event (e.g. from the sidebar button) to (re)start the tour. */
export const TOUR_START_EVENT = 'sente:tour:start';

/** Fired when the tour completes; the Identities page opens the New Identity modal on it. */
export const OPEN_NEW_IDENTITY_EVENT = 'sente:new-identity:open';

const storageKey = (orgId: string) => `sente:onboarding:${orgId}`;

const Code: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <code className="font-mono text-xs bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-slate-200 rounded px-1.5 py-0.5">
    {children}
  </code>
);

interface TourStep {
  id: string;
  title: string;
  body: React.ReactNode;
  /** Route to be on for this step; null/undefined = stay wherever we are. */
  route?: (ctx: { identityId: string | null }) => string | null;
  /** data-tour attribute value to spotlight; absent = centered card. */
  target?: string;
}

const STEPS: TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Sente 👋',
    body: (
      <>
        <p className="mb-2">
          Sente gives your AI agents their own email identities — and accounts that keep working.
        </p>
        <p>
          This two-minute tour walks the loop end to end: create an identity, watch it receive and
          parse verification email, add an account at an app, then automate it all from code.
        </p>
      </>
    ),
  },
  {
    id: 'identities',
    title: 'Identities',
    route: () => '/',
    target: 'nav-identities',
    body: (
      <p>
        An <strong>identity</strong> is one managed email address your org owns and your agent
        operates — like <Code>my-agent@sente.run</Code>. It's long-lived: one identity can hold
        accounts at many apps, and it stays reachable month after month.
      </p>
    ),
  },
  {
    id: 'new-identity',
    title: 'Create your first identity',
    route: () => '/',
    target: 'new-identity',
    body: (
      <p>
        Use the <strong>New Identity</strong> button to create your first one — give it a name and,
        optionally, a custom address. It's live immediately on the free tier, no card needed.
      </p>
    ),
  },
  {
    id: 'inbox',
    title: 'A real inbox your agent can read',
    route: ctx => (ctx.identityId ? `/identities/${ctx.identityId}` : null),
    target: 'inbox',
    body: (
      <>
        <p className="mb-2">
          Every email that arrives is parsed automatically: OTP codes and magic links are extracted
          server-side and show up as badges on each message. Your agent gets the code with one
          blocking call — <Code>sente wait --otp</Code> — no webmail scraping, no regex.
        </p>
        <p>Try it later: open the identity, hit Compose, or email its address from anywhere.</p>
      </>
    ),
  },
  {
    id: 'new-account',
    title: 'Add an account at an app',
    route: ctx => (ctx.identityId ? `/identities/${ctx.identityId}` : null),
    target: 'new-account',
    body: (
      <>
        <p className="mb-2">
          <strong>Connect</strong> an account you already own — credentials go into a write-only
          vault and Sente re-logs-in when the session dies (autonomous for authenticator/TOTP 2FA).
          Or <strong>Register</strong> a new one — the managed browser fills the signup and
          completes email verification from this inbox; with confirm-before-submit, you review and
          click the final submit.
        </p>
        <p className="text-xs text-gray-500 dark:text-slate-400">
          Honest limits: email/SMS MFA still needs a human today, and CAPTCHAs always stop for a
          human.
        </p>
      </>
    ),
  },
  {
    id: 'activity',
    title: 'Watch every run',
    route: () => '/activity',
    target: 'activity-header',
    body: (
      <p>
        Registers, logins, and connects show up here as runs with a live browser view. When a run
        needs you — an MFA code, the final submit — it pauses: open it, take over in the live view,
        then resume and the agent continues.
      </p>
    ),
  },
  {
    id: 'api-keys',
    title: 'Automate it from code',
    route: () => '/api-keys',
    target: 'api-tokens',
    body: (
      <>
        <p className="mb-2">
          Create an <Code>sk_sente_</Code> token and drive everything from the CLI or SDKs:{' '}
          <Code>npm i -g @sente-labs/cli && sente login</Code>.
        </p>
        <p>
          Even faster: point your coding agent at <Code>sente.run/skill.md</Code> — it onboards
          itself.
        </p>
      </>
    ),
  },
  {
    id: 'done',
    title: "That's the loop",
    route: () => '/',
    body: (
      <>
        <p className="mb-2">
          Identity → verified email → an account that still works next month. Replay this tour
          anytime from the sidebar.
        </p>
        <p className="mb-2">
          Docs live at <Code>sente.run/skill.md</Code> — and <Code>support@sente.run</Code> is a
          human.
        </p>
        <p>Hit Finish and we'll open the New Identity form so you can create your first one.</p>
      </>
    ),
  },
];

const PAD = 8; // spotlight padding around the target, px

function visibleRect(el: HTMLElement): DOMRect | null {
  const r = el.getBoundingClientRect();
  const visible =
    r.width > 4 && r.height > 4 && r.right > 0 && r.left < window.innerWidth && r.bottom > 0 && r.top < window.innerHeight + r.height;
  return visible ? r : null;
}

const OnboardingTour: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { currentOrganization } = useOrganization();
  const { ready, listIdentities } = useSente();
  const navigate = useNavigate();
  const location = useLocation();

  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  // undefined = not fetched yet; null = org has no identities
  const [identityId, setIdentityId] = useState<string | null | undefined>(undefined);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [targetMissing, setTargetMissing] = useState(false);
  const [tipPos, setTipPos] = useState<{ top: number; left: number } | null>(null);

  const targetElRef = useRef<HTMLElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);

  const step = STEPS[idx];
  const orgId = currentOrganization?.id ?? null;

  const finish = useCallback(
    (status: 'done' | 'skipped') => {
      setActive(false);
      setIdx(0);
      if (orgId) {
        localStorage.setItem(storageKey(orgId), JSON.stringify({ status, at: new Date().toISOString() }));
      }
      // The done step already lives on '/', so Identities is mounted and listening.
      if (status === 'done') {
        window.dispatchEvent(new Event(OPEN_NEW_IDENTITY_EVENT));
      }
    },
    [orgId],
  );

  // Auto-start once per org, only from the landing page so we never interrupt a deep link.
  useEffect(() => {
    if (!isAuthenticated || !orgId || active) return;
    if (location.pathname !== '/') return;
    if (localStorage.getItem(storageKey(orgId))) return;
    const t = setTimeout(() => setActive(true), 800);
    return () => clearTimeout(t);
  }, [isAuthenticated, orgId, location.pathname, active]);

  // Manual (re)start from anywhere, e.g. the sidebar "Product tour" button.
  useEffect(() => {
    const handler = () => {
      setIdx(0);
      setActive(true);
    };
    window.addEventListener(TOUR_START_EVENT, handler);
    return () => window.removeEventListener(TOUR_START_EVENT, handler);
  }, []);

  // Identity ids are org-scoped — drop any cached one when the org changes.
  useEffect(() => {
    setIdentityId(undefined);
  }, [orgId]);

  // Resolve the first identity so identity-scoped steps can deep-link to it.
  useEffect(() => {
    if (!active || !ready) return;
    setIdentityId(undefined); // a restart must not reuse a stale (possibly deleted) id
    let cancelled = false;
    listIdentities()
      .then(ids => !cancelled && setIdentityId(ids[0]?.id ?? null))
      .catch(() => !cancelled && setIdentityId(null));
    return () => {
      cancelled = true;
    };
  }, [active, ready, listIdentities]);

  // Navigate to the step's route when needed.
  useEffect(() => {
    if (!active) return;
    const wanted = step.route ? step.route({ identityId: identityId ?? null }) : null;
    if (wanted && location.pathname !== wanted) navigate(wanted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, idx, identityId]);

  // Find + track the target element (pages fetch data, so poll briefly before giving up).
  useEffect(() => {
    if (!active) return;
    setRect(null);
    setTipPos(null);
    setTargetMissing(false);
    targetElRef.current = null;
    if (!step.target) return;

    // Identity-scoped step in an org with zero identities: the route (and target) can never
    // exist — go straight to the centered card instead of polling. While the identity fetch is
    // still in flight (identityId === undefined) keep the normal grace-period poll below.
    if (step.route && identityId !== undefined && step.route({ identityId: identityId ?? null }) === null) {
      setTargetMissing(true);
      return;
    }

    let cancelled = false;
    let tries = 0;
    let gotRect = false;
    let interval: ReturnType<typeof setInterval> | null = null;
    let visibilityDeadline: ReturnType<typeof setTimeout> | null = null;

    const measure = () => {
      const el = targetElRef.current;
      if (!el || !document.contains(el)) return;
      const r = visibleRect(el);
      if (r) gotRect = true;
      setRect(prev => {
        if (!r) return prev;
        if (
          prev &&
          Math.abs(prev.top - r.top) < 1 &&
          Math.abs(prev.left - r.left) < 1 &&
          Math.abs(prev.width - r.width) < 1 &&
          Math.abs(prev.height - r.height) < 1
        ) {
          return prev;
        }
        return r;
      });
    };

    const find = () => {
      if (cancelled) return;
      const el = document.querySelector(`[data-tour="${step.target}"]`) as HTMLElement | null;
      const size = el?.getBoundingClientRect();
      if (el && size && size.width > 4 && size.height > 4) {
        targetElRef.current = el;
        // Accept off-screen elements too: scrollIntoView recovers targets above/below the fold.
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        // Track while the smooth scroll settles and across layout shifts.
        setTimeout(measure, 50);
        interval = setInterval(measure, 200);
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);
        // In the DOM but never on screen (e.g. off-canvas sidebar below lg): fall back to the
        // centered card instead of hanging behind a dimmed screen.
        visibilityDeadline = setTimeout(() => {
          if (!cancelled && !gotRect) setTargetMissing(true);
        }, 1500);
      } else if (++tries < 20) {
        setTimeout(find, 100);
      } else {
        setTargetMissing(true);
      }
    };
    find();

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      if (visibilityDeadline) clearTimeout(visibilityDeadline);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, idx, location.pathname, identityId]);

  // Position the tooltip near the spotlight (below if it fits, else above), clamped to viewport.
  useLayoutEffect(() => {
    if (!active) return;
    const tip = tipRef.current;
    if (!tip) return;
    if (!rect) {
      setTipPos(null);
      return;
    }
    const th = tip.offsetHeight;
    const tw = tip.offsetWidth;
    const fitsBelow = rect.bottom + PAD + 12 + th <= window.innerHeight - 12;
    const top = fitsBelow ? rect.bottom + PAD + 12 : Math.max(12, rect.top - PAD - 12 - th);
    const left = Math.min(Math.max(12, rect.left + rect.width / 2 - tw / 2), Math.max(12, window.innerWidth - tw - 12));
    setTipPos({ top, left });
  }, [active, rect, idx]);

  // The click shield only blocks pointers — take keyboard focus away from whatever had it
  // (e.g. an input in a modal buried under the overlay) and keep it inside the card.
  useEffect(() => {
    if (!active) return;
    (document.activeElement as HTMLElement | null)?.blur?.();
  }, [active]);

  useEffect(() => {
    if (!active) return;
    tipRef.current?.focus();
  }, [active, idx]);

  // Keyboard: Esc dismisses, arrows navigate, Tab stays inside the card.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        finish('skipped');
        return;
      }
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (e.key === 'ArrowRight' && idx < STEPS.length - 1) setIdx(i => i + 1);
      if (e.key === 'ArrowLeft' && idx > 0) setIdx(i => i - 1);
      if (e.key === 'Tab') {
        const card = tipRef.current;
        if (!card) return;
        const focusables = Array.from(card.querySelectorAll<HTMLElement>('button'));
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const activeEl = document.activeElement as HTMLElement | null;
        if (!activeEl || !card.contains(activeEl)) {
          e.preventDefault();
          first.focus();
        } else if (!e.shiftKey && activeEl === last) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && activeEl === first) {
          e.preventDefault();
          last.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, idx, finish]);

  if (!active || !isAuthenticated || !currentOrganization) return null;

  const isLast = idx === STEPS.length - 1;
  const spotlight = step.target && rect && !targetMissing;
  const centered = !step.target || targetMissing;
  const searching = Boolean(step.target) && !rect && !targetMissing;

  const card = (
    <div
      ref={tipRef}
      tabIndex={-1}
      className="w-[calc(100vw-2rem)] max-w-sm bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-2xl p-5 outline-none"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-slate-500">
          {idx + 1} of {STEPS.length}
        </span>
        <button
          onClick={() => finish('skipped')}
          className="text-xs text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 transition-colors"
        >
          Skip tour
        </button>
      </div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">{step.title}</h3>
      <div className="text-sm text-gray-600 dark:text-slate-300 leading-relaxed">{step.body}</div>
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-1.5">
          {STEPS.map((s, i) => (
            <span
              key={s.id}
              className={`w-1.5 h-1.5 rounded-full ${i === idx ? 'bg-blue-600 dark:bg-blue-400' : 'bg-gray-200 dark:bg-slate-700'}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          {idx > 0 && (
            <button
              onClick={() => setIdx(i => i - 1)}
              className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              Back
            </button>
          )}
          <button
            onClick={() => (isLast ? finish('done') : setIdx(i => i + 1))}
            className="px-4 py-1.5 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            {isLast ? 'Finish' : idx === 0 ? 'Start tour' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[300]" role="dialog" aria-modal="true" aria-label="Product tour">
      {/* Click shield: blocks interaction with the page while the tour runs. */}
      <div
        className={`absolute inset-0 ${spotlight || searching ? '' : 'bg-slate-950/60'}`}
        onClick={e => e.stopPropagation()}
      />
      {/* Spotlight: pass-through ring whose giant shadow dims everything else. */}
      {spotlight && rect && (
        <div
          className="absolute rounded-xl border-2 border-blue-500 pointer-events-none transition-all duration-200"
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            boxShadow: '0 0 0 9999px rgba(2, 6, 23, 0.6)',
          }}
        />
      )}
      {/* While searching for a target, dim softly so the page doesn't flash. */}
      {searching && <div className="absolute inset-0 bg-slate-950/40" />}

      {centered ? (
        <div className="absolute inset-0 flex items-center justify-center p-4">{card}</div>
      ) : (
        <div
          className={`absolute ${tipPos ? '' : 'opacity-0 pointer-events-none'}`}
          style={tipPos ? { top: tipPos.top, left: tipPos.left } : { top: 0, left: 0 }}
        >
          {card}
        </div>
      )}
    </div>
  );
};

export default OnboardingTour;
